import { encodeContent } from "@rootreeweb/form-bridge";
import { getPrettyFieldName, getPrettyValue } from "@rootreeweb/js-lib";

import { getTranslation } from "../../helpers";

export type DataForEmail = {
	details: Map<string, boolean | string>;
	notes: string;
	product: string;
	properties: Map<string, boolean | string>;
};
/**
 * Generates an email-friendly version of the selection summary.
 *
 * @param data The data to generate the email-friendly version of.
 * @returns String An email-friendly version of the selection summary.
 */
export function createEmailPayload(data: DataForEmail): string {
	const { details, notes, product, properties } = data;

	const fields = [
		{ label: getTranslation("product"), value: product + "<br/>" },
		{
			label: getTranslation("details"),
			value: generateOptionsText(details),
		},
		{
			label: getTranslation("options"),
			value: generateOptionsText(properties),
		},
		{
			label: getTranslation("notes"),
			value: notes,
		},
	];

	let output = "";
	for (const field of fields) {
		const { value } = field;
		if (!value) continue;
		output += `${createLineItem(field, true)}\n`;
	}

	return encodeContent(output);
}

/**
 * Creates a line item for the email-friendly version of the selection summary.
 *
 * @param field The field to create a line item for.
 * @param field.label The label of the field.
 * @param field.value The value of the field.
 * @param breakAfterLabel Whether to break after the label.
 * @returns String The line item for the email-friendly version of the selection
 *   summary.
 */
function createLineItem(
	field: { label: string; value: string },
	breakAfterLabel?: boolean,
): string {
	const { label, value } = field;
	if (!value) return "";
	const cleanedLabel = `<strong>${getPrettyFieldName(label)}:</strong>${
		breakAfterLabel ? "<br/>" : ""
	}`;

	return `${cleanedLabel} ${value}`;
}

/**
 * Generates the options portion of the friendly text.
 *
 * @param fields The fields to generate the options portion of the friendly text
 * @returns String The options portion of the friendly text.
 */
function generateOptionsText(fields: Map<string, boolean | string>): string {
	let list = "";
	for (const [label, rawValue] of Array.from(fields.entries())) {
		const value = getPrettyValue(rawValue);
		list += `	${createLineItem({ label, value })}\n`;
	}
	return list;
}
